/**
 * M24 task-09 — DB Connectors list (DBC-01; HUB-079 §4). Family-A refine
 * `useTable` → `<DataTable>` (tracks/List precedent). Columns are exported
 * (module-level, no callbacks) so the no-jsdom tests can render them through
 * a raw `useReactTable` harness.
 */
import { useTable } from '@refinedev/react-table';
import { type ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router';
import type { DbConnectorItem } from '@verybigthings/trackframe-shared/schemas';
import { buttonVariants } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/DataTable';
import { TableSearch } from '@/components/TableSearch';
import { PageHeader } from '@/components/PageHeader';
import { fmtDateTime } from '@/lib/format';
import { useSearchFilter } from '@/lib/use-search-filter';
import { cn } from '@/lib/utils';

export function ConnectorStatusBadge({ status }: { status: DbConnectorItem['status'] }) {
  return (
    <Badge variant={status === 'active' ? 'success' : 'destructive'} className="rounded-full">
      {status}
    </Badge>
  );
}

const SCHEMA_STATUS_VARIANT = {
  pending: 'muted',
  generating: 'secondary',
  ready: 'success',
  error: 'destructive',
} as const;

export function SchemaStatusBadge({ status }: { status: DbConnectorItem['schemaStatus'] }) {
  return (
    <Badge variant={SCHEMA_STATUS_VARIANT[status]} className="rounded-full">
      {status}
    </Badge>
  );
}

export const dbConnectorColumns: ColumnDef<DbConnectorItem>[] = [
  {
    id: 'name',
    header: 'Name',
    accessorKey: 'name',
    cell: (c) => (
      <Link to={`/admin/db-connectors/${c.row.original.id}`} className="font-medium underline-offset-2 hover:underline">
        {c.row.original.name}
      </Link>
    ),
  },
  {
    id: 'engine',
    header: 'Engine',
    accessorKey: 'engine',
    cell: (c) => <code className="font-mono text-xs">{c.getValue() as string}</code>,
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    cell: (c) => <ConnectorStatusBadge status={c.getValue() as DbConnectorItem['status']} />,
  },
  {
    id: 'schemaStatus',
    header: 'Schema',
    accessorKey: 'schemaStatus',
    cell: (c) => <SchemaStatusBadge status={c.getValue() as DbConnectorItem['schemaStatus']} />,
  },
  {
    id: 'activeSchemaVersion',
    header: 'Active version',
    accessorKey: 'activeSchemaVersion',
    cell: (c) => {
      const v = c.getValue() as number | null;
      return v === null ? <span className="text-muted-foreground">—</span> : <span className="tabular-nums">v{v}</span>;
    },
  },
  {
    id: 'lastSyncedAt',
    header: 'Last synced',
    accessorKey: 'lastSyncedAt',
    cell: (c) => (
      <span className="tabular-nums text-muted-foreground">{fmtDateTime(c.getValue() as string | null)}</span>
    ),
  },
  {
    id: 'lastError',
    header: 'Last error',
    accessorKey: 'lastError',
    cell: (c) => {
      const err = c.getValue() as string | null;
      if (!err) return <span className="text-muted-foreground">—</span>;
      return (
        <span className="block max-w-56 truncate text-destructive" title={err}>
          {err}
        </span>
      );
    },
  },
  {
    id: 'actions',
    header: '',
    cell: (c) => (
      <div className="flex justify-end">
        <Link
          to={`/admin/db-connectors/${c.row.original.id}`}
          className={cn(buttonVariants({ size: 'sm', variant: 'ghost' }))}
        >
          View
        </Link>
      </div>
    ),
  },
];

export function DbConnectorsList() {
  const {
    reactTable: table,
    refineCore: { setFilters, filters },
  } = useTable<DbConnectorItem>({ columns: dbConnectorColumns });
  const [search, setSearch] = useSearchFilter(setFilters, filters);

  return (
    <div className="space-y-4">
      <PageHeader
        title="DB Connectors"
        description="Read-only database connections with an LLM-generated, hand-editable semantic layer."
        actions={[{ label: 'New connector', to: '/admin/db-connectors/create' }]}
      />
      <DataTable
        table={table}
        toolbar={<TableSearch value={search} onChange={setSearch} placeholder="Filter by name or engine…" />}
      />
    </div>
  );
}
